import { format } from 'date-fns'
import type { PropertyCalendar } from '@/api/client'
import { monthParamToDate } from '@/lib/calendar-month'
import { CALENDAR_LAYERS, summarizeLayer, type CalendarLayerId, type LayerSummary } from './layers'

/**
 * What the property calendar knows about its own data, in one place.
 *
 * The calendar depends on three reads that settle at different times:
 *
 * - the month read (`usePropertyCalendar`): all seven layers in one response, so
 *   they settle together;
 * - the crew list (`['cleaning', 'teams']`): a cleaning bar's crew name and
 *   colour, which can land after the bars it belongs to;
 * - the month's nightly prices: best-effort, drawn in the quiet days' corners.
 *
 * Everything that shows or withholds something because data is on its way - the
 * chips, the toolbar summary, the placeholder bands, the empty-state claims, the
 * sheets - reads from this and decides nothing for itself.
 */

/** Where one read stands. */
export type ReadState = 'loading' | 'ready' | 'error'

/** A layer as the grid sees it: its read, or `hidden` when the viewer switched it off. */
export type LayerState = ReadState | 'hidden'

export interface LayerReadiness {
  /** The layer's own read, whether or not it is switched on (a chip still shows its count). */
  read: ReadState
  /** What the grid, the lists and the empty-state claims go by. */
  state: LayerState
}

export interface CalendarReadiness {
  layers: Record<CalendarLayerId, LayerReadiness>
  /**
   * What each chip may say: a count only for a layer that was read for the month
   * on screen. `pending` while loading and while the grid still holds the last
   * month (its counts are that month's, not this one's).
   */
  summaries: Record<CalendarLayerId, LayerSummary>
  /** The month read for the month on screen has not landed (first load, or paging). */
  monthLoading: boolean
  /** The month read failed outright: there is no layer to show, only the failure. */
  monthFailed: boolean
  /**
   * The grid is still showing the previous month (`keepPreviousData`), while the
   * month asked for loads. Its bars are that month's, so it is dimmed and inert.
   */
  stale: boolean
  /** Crew names and colours for the cleaning bars. */
  crew: ReadState
  /** Nightly prices; `off` where none are asked for (the phone's agenda). */
  prices: ReadState | 'off'
  /** What is still on its way, in plain words, for the toolbar. Empty once everything settled. */
  outstanding: string[]
  /** `outstanding`, as one sentence ("Loading July 2026 and crew names"), or null once settled. */
  summaryText: string | null
  /** The month on screen, spelled out ("July 2026"). */
  monthLabel: string
  /** Every switched-on layer was read for the month on screen: an empty result can be believed. */
  enabledLayersReady: boolean
}

interface QueryState {
  isPending: boolean
  isError: boolean
}

export interface ReadinessInput {
  /** `YYYY-MM`, the month on screen. */
  month: string
  /** The month read's data: this month's, or - while paging - the last one's. */
  calendar: PropertyCalendar | undefined
  /** The data above is the last month's, kept while this one loads. */
  isPlaceholderData: boolean
  monthQuery: QueryState
  teamsQuery: QueryState
  /** Omit for a screen that does not ask for prices. */
  pricesQuery?: QueryState
  isVisible: (id: CalendarLayerId) => boolean
}

function readOf(query: QueryState): ReadState {
  return query.isPending ? 'loading' : query.isError ? 'error' : 'ready'
}

/** "a", "a and b", "a, b and c". */
function sentence(parts: string[]): string {
  if (parts.length <= 1) return parts.join('')
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

export function deriveReadiness({
  month,
  calendar,
  isPlaceholderData,
  monthQuery,
  teamsQuery,
  pricesQuery,
  isVisible,
}: ReadinessInput): CalendarReadiness {
  const stale = isPlaceholderData && !!calendar
  // Data this month's own, or none: the last month's is not an answer for it.
  const own = stale ? undefined : calendar
  const monthFailed = !calendar && monthQuery.isError
  const monthLoading = !own && !monthFailed

  const summaries = {} as Record<CalendarLayerId, LayerSummary>
  const layers = {} as Record<CalendarLayerId, LayerReadiness>
  for (const { id } of CALENDAR_LAYERS) {
    const summary: LayerSummary = own ? summarizeLayer(own, id, month) : { status: 'pending' }
    summaries[id] = summary
    const read: ReadState =
      summary.status === 'ok' ? 'ready' : summary.status === 'error' ? 'error' : 'loading'
    layers[id] = { read, state: isVisible(id) ? read : 'hidden' }
  }

  const crew = readOf(teamsQuery)
  const prices = pricesQuery ? readOf(pricesQuery) : 'off'
  const monthLabel = format(monthParamToDate(month), 'MMMM yyyy')

  const outstanding: string[] = []
  if (monthLoading) outstanding.push(monthLabel)
  // The crew only matters while cleanings are on the screen.
  if (crew === 'loading' && isVisible('cleaning')) outstanding.push('crew names')
  if (prices === 'loading') outstanding.push('nightly prices')

  return {
    layers,
    summaries,
    monthLoading,
    monthFailed,
    stale,
    crew,
    prices,
    outstanding,
    summaryText: outstanding.length > 0 ? `Loading ${sentence(outstanding)}` : null,
    monthLabel,
    enabledLayersReady: CALENDAR_LAYERS.every(
      ({ id }) => layers[id].state === 'hidden' || layers[id].state === 'ready'
    ),
  }
}
